import { useContext, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useAppointmentQuery } from "../../api/fetchAppointment"
import { useHealthcareServiceQuery } from "../../api/fetchHealthcareService"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { PageFunction } from "../../types"
import { AppointmentCard } from "../appointment/appointmentViews/AppointmentCard"
import { QuestionnaireComplete } from "./QuestionnaireComplete"
import { useLocationQuery } from "../../api/fetchLocation"

/**
 * If no questionnaire exists for the patient action, we default to displaying a "Contact Us" page.
 *
 * @returns {JSX.Element | null} The rendered page.
 */
export const NoQuestionnaire = (): JSX.Element | null => {
  const params = useParams()
  const { token } = useContext(AuthenticationContext)
  const { changeRequested } = useContext(AuthenticationContext)

  const appointmentQuery = useAppointmentQuery({
    appointmentId: params.appointmentId,
    token,
  })

  const healthcareServiceQuery = useHealthcareServiceQuery({
    odsId: params.odsId,
    token,
  })
  const healthcareService = healthcareServiceQuery.data

  const appointment = appointmentQuery.data
  const locationId = appointment?.locationId

  const { data: location } = useLocationQuery({ locationId, token })

  const pageFunction: PageFunction = useMemo(
    () => (params.patientAction === "reschedule" ? "reschedule" : "cancel"),
    [params.patientAction],
  )

  useEffect(() => {
    if (pageFunction) {
      const newTitle = `Ask to ${pageFunction} your appointment`
      document.title = newTitle
    }
  }, [pageFunction])

  if (changeRequested) return <QuestionnaireComplete />

  if (!healthcareService || !appointment) return null

  const pageHeader = `Ask to ${pageFunction} your appointment`
  return (
    <>
      <h1>{pageHeader}</h1>
      <AppointmentCard
        appointment={appointment}
        appointmentView
        healthcareService={healthcareService}
        showPageFunctionContactMessage
        pageFunction={pageFunction}
        location={location}
      />
    </>
  )
}
